
// ######################################################################
// # THIS FILE HAS BEEN REPURPOSED FOR SUPABASE INITIALIZATION          #
// ######################################################################
//
// Despite the filename, this file no longer initializes Firebase.
// It now creates and exports the singleton instance of the Supabase client.

import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { supabaseConfig } from './firebase.config'; // Repurposed for Supabase config

// A robust check to see if the config is real and not the placeholder.
export function isSupabaseConfigured(): boolean {
  const url = supabaseConfig.url;
  const key = supabaseConfig.anonKey;

  // Check if values exist, are strings, and are NOT the default placeholders
  return (
    typeof url === 'string' &&
    url.length > 0 &&
    url !== "YOUR_SUPABASE_URL_HERE" &&
    url !== "YOUR_SUPABASE_URL" &&
    !url.includes("YOUR_SUPABASE_URL") &&
    typeof key === 'string' &&
    key.length > 0 &&
    key !== "YOUR_SUPABASE_ANON_KEY_HERE" &&
    key !== "YOUR_SUPABASE_ANON_KEY" &&
    !key.includes("YOUR_SUPABASE_ANON_KEY")
  );
}

let supabaseInstance: SupabaseClient | null = null;

function initializeSupabase() {
  if (isSupabaseConfigured()) {
    // Prevent re-initialization
    if (supabaseInstance) {
      return;
    }
    try {
      console.log('Supabase configuration found. Attempting to initialize...');
      supabaseInstance = createClient(supabaseConfig.url, supabaseConfig.anonKey, {
        auth: {
            detectSessionInUrl: true,
            persistSession: true,
            autoRefreshToken: true,
            storageKey: 'supabase.auth.token',
        },
        global: {
          fetch: async (url, options) => {
            const headers = new Headers(options?.headers);
            const pinToken = localStorage.getItem('custom_pin_token');
            
            if (pinToken) {
              // 1. Pre-flight Check: Is it expired locally?
              try {
                  const payload = JSON.parse(atob(pinToken.split('.')[1]));
                  const now = Math.floor(Date.now() / 1000);
                  
                  if (payload.exp && payload.exp < now) {
                      console.warn("[Auth] Token expired locally. Removing.");
                      localStorage.removeItem('custom_pin_token');
                      // Don't send header, let it fail or be anonymous
                  } else {
                      headers.set('Authorization', `Bearer ${pinToken}`);
                  }
              } catch (e) {
                  console.error("[Auth] Malformed PIN Token. Removing.");
                  localStorage.removeItem('custom_pin_token');
              }
            }
            
            // 2. Execute Request
            const response = await fetch(url, { ...options, headers });

            // 3. Post-flight Check: Did the server reject us? (The 401 Interceptor)
            if (response.status === 401) {
                console.error("[Auth] 401 Unauthorized detected. Destroying session.");
                
                // Only clear if we actually had a token, to avoid loop on public APIs
                if (localStorage.getItem('custom_pin_token')) {
                    localStorage.removeItem('custom_pin_token');
                    
                    // Force reload to clear memory state if we are not already on login page
                    if (!window.location.hash.includes('/login') && !window.location.hash.endsWith('#/')) {
                        window.location.href = '/'; 
                    }
                }
            }

            return response;
          },
        },
      });
      console.log("Supabase initialized successfully.");
    } catch (e) {
      console.error("Supabase initialization failed:", e);
      supabaseInstance = null;
      console.warn("Falling back to Mock Mode due to initialization error.");
    }
  } else {
    console.warn('Supabase not configured. Using mock data.');
  }
}

// A singleton getter function to get the initialized Supabase instance.
export function getSupabase(): SupabaseClient | null {
  return supabaseInstance;
}

// Eagerly initialize Supabase on module load.
initializeSupabase();
